import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  User,
  KeyRound,
  Eye,
  EyeOff,
  ShieldCheck,
  Loader2,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import api from '../../api';

/**
 * Page admin "Mon compte" — permet de :
 *  - Voir l'identité de l'admin connecté (email)
 *  - Changer son mot de passe (avec validation côté client + serveur)
 *
 * Accessible depuis la sidebar admin (icône User) sur mobile et desktop.
 */
export default function AdminProfilePage() {
  const adminEmail = (() => {
    try {
      const payload = localStorage.getItem('token')?.split('.')[1];
      if (payload) return JSON.parse(atob(payload)).sub;
    } catch (e) { console.warn('[Profile] JWT decode failed:', e); }
    return null;
  })();

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-profile-page">
      <div className="flex items-center gap-3">
        <User className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-black">Mon compte</h1>
          <p className="text-sm text-muted-foreground">
            Gestion du compte administrateur connecté
          </p>
        </div>
      </div>

      <Card data-testid="admin-profile-identity-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="w-4 h-4" />
            Identité
          </CardTitle>
          <CardDescription>Compte actuellement connecté</CardDescription>
        </CardHeader>
        <CardContent className="text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-y-1.5 gap-x-3">
            <span className="font-semibold text-muted-foreground">Email</span>
            <code className="font-mono text-foreground bg-muted px-1.5 py-0.5 rounded w-fit text-[12px]">
              {adminEmail || '—'}
            </code>
            <span className="font-semibold text-muted-foreground">Rôle</span>
            <span className="text-foreground">Administrateur</span>
          </div>
        </CardContent>
      </Card>

      <ChangePasswordCard />
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);

  // Validation côté client (miroir des règles serveur)
  const rules = {
    minLength: next.length >= 10,
    hasLetter: /[a-zA-Z]/.test(next),
    hasDigit: /\d/.test(next),
    hasSpecial: /[^a-zA-Z0-9]/.test(next),
    matches: next && next === confirm,
    differentFromCurrent: !!next && next !== current,
  };
  const allValid = Object.values(rules).every(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!allValid) {
      toast.error('Toutes les conditions de sécurité ne sont pas remplies');
      return;
    }
    setLoading(true);
    try {
      await api.adminChangePassword(current, next);
      toast.success('Mot de passe modifié avec succès. Reconnectez-vous au prochain login.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err.message || 'Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="admin-profile-change-password-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="w-4 h-4" />
          Changer mon mot de passe
        </CardTitle>
        <CardDescription>
          Minimum 10 caractères, au moins 1 lettre, 1 chiffre et 1 caractère spécial.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4" autoComplete="off">
          <PasswordField
            id="cp-current"
            label="Mot de passe actuel"
            value={current}
            onChange={setCurrent}
            show={showCurrent}
            onToggle={() => setShowCurrent(v => !v)}
            placeholder="••••••••"
            testid="cp-current-input"
            autoComplete="current-password"
          />
          <PasswordField
            id="cp-new"
            label="Nouveau mot de passe"
            value={next}
            onChange={setNext}
            show={showNext}
            onToggle={() => setShowNext(v => !v)}
            placeholder="Saisissez un nouveau mot de passe robuste"
            testid="cp-new-input"
            autoComplete="new-password"
          />
          <PasswordField
            id="cp-confirm"
            label="Confirmer le nouveau mot de passe"
            value={confirm}
            onChange={setConfirm}
            show={showNext}
            onToggle={() => setShowNext(v => !v)}
            placeholder="Resaisissez le nouveau mot de passe"
            testid="cp-confirm-input"
            autoComplete="new-password"
          />

          {/* Indicateurs de validation en temps réel */}
          {next && (
            <div
              className="rounded-md border border-border bg-muted/30 p-3 text-xs space-y-1"
              data-testid="cp-validation-checklist"
            >
              <RuleLine ok={rules.minLength} label="Au moins 10 caractères" />
              <RuleLine ok={rules.hasLetter} label="Contient une lettre" />
              <RuleLine ok={rules.hasDigit} label="Contient un chiffre" />
              <RuleLine ok={rules.hasSpecial} label="Contient un caractère spécial" />
              <RuleLine
                ok={rules.matches}
                label="Confirmation identique"
                hint={!rules.matches && confirm ? 'Les mots de passe ne correspondent pas' : null}
              />
              <RuleLine
                ok={rules.differentFromCurrent}
                label="Différent du mot de passe actuel"
              />
            </div>
          )}

          <div className="pt-2 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
            <p className="text-[11px] text-muted-foreground sm:max-w-md">
              Astuce sécurité : utilisez un gestionnaire de mots de passe (Bitwarden, 1Password…) pour générer un mot de passe aléatoire de 16+ caractères.
            </p>
            <Button
              type="submit"
              disabled={!allValid || loading || !current}
              className="sm:min-w-[200px]"
              data-testid="cp-submit-btn"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Modification…</>
                : <><KeyRound className="w-4 h-4 mr-2" />Mettre à jour</>}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function PasswordField({ id, label, value, onChange, show, onToggle, placeholder, testid, autoComplete }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-semibold">{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          data-testid={testid}
          className="pr-10 font-mono"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label={show ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          tabIndex={-1}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function RuleLine({ ok, label, hint }) {
  return (
    <div className={`flex items-center gap-2 ${ok ? 'text-emerald-600' : 'text-muted-foreground'}`}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5 opacity-60" />}
      <span>{label}</span>
      {hint && <span className="text-destructive ml-1 text-[10px]">({hint})</span>}
    </div>
  );
}
